import { useEffect, useState } from 'react';
import { Film, CheckCircle2, XCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// SPEC-021: client-side URL validator + thumbnail preview for YouTube /
// Vimeo embeds. Mirrors the server-side regex set in
// internal/services/content/oembed.go so invalid URLs fail fast before a
// save call. The backend re-validates on submit regardless.

const YOUTUBE_RE = [
  /^(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?(?:[^#]*&)*v=([A-Za-z0-9_-]{11})/i,
  /^(?:https?:\/\/)?youtu\.be\/([A-Za-z0-9_-]{11})/i,
  /^(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([A-Za-z0-9_-]{11})/i,
];
const VIMEO_RE = [
  /^(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(\d+)/i,
  /^(?:https?:\/\/)?player\.vimeo\.com\/video\/(\d+)/i,
];

interface EmbedParseResult {
  provider: 'youtube' | 'vimeo';
  videoId: string;
  canonicalURL: string;
}

function parseEmbed(raw: string): EmbedParseResult | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  // Reject obvious playlists — matches the server-side guard.
  if (/[?&]list=/i.test(trimmed) && /youtube\.com\/watch/i.test(trimmed)) {
    return null;
  }
  for (const re of YOUTUBE_RE) {
    const m = re.exec(trimmed);
    if (m) {
      return {
        provider: 'youtube',
        videoId: m[1],
        canonicalURL: `https://www.youtube.com/watch?v=${m[1]}`,
      };
    }
  }
  for (const re of VIMEO_RE) {
    const m = re.exec(trimmed);
    if (m) {
      return {
        provider: 'vimeo',
        videoId: m[1],
        canonicalURL: `https://vimeo.com/${m[1]}`,
      };
    }
  }
  return null;
}

interface VideoEmbedInputProps {
  value: string | null | undefined;
  onChange: (canonicalURL: string | null) => void;
}

export function VideoEmbedInput({ value, onChange }: VideoEmbedInputProps) {
  const [raw, setRaw] = useState(value ?? '');
  const [parsed, setParsed] = useState<EmbedParseResult | null>(() => parseEmbed(value ?? ''));

  // Sync external changes (e.g. loading an existing item).
  useEffect(() => {
    setRaw(value ?? '');
    setParsed(parseEmbed(value ?? ''));
  }, [value]);

  const onInput = (v: string) => {
    setRaw(v);
    const p = parseEmbed(v);
    setParsed(p);
    onChange(p?.canonicalURL ?? (v.trim() === '' ? null : raw));
  };

  const thumb =
    parsed?.provider === 'youtube'
      ? `https://img.youtube.com/vi/${parsed.videoId}/hqdefault.jpg`
      : null;

  return (
    <div className="space-y-2">
      <Label htmlFor="embed-url">Video embed URL</Label>
      <div className="flex items-center gap-2">
        <Input
          id="embed-url"
          value={raw}
          placeholder="https://www.youtube.com/watch?v=... or https://vimeo.com/..."
          onChange={(e) => onInput(e.target.value)}
        />
        {raw.trim() !== '' &&
          (parsed ? (
            <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />
          ) : (
            <XCircle className="h-4 w-4 shrink-0 text-destructive" />
          ))}
      </div>
      {parsed && (
        <div className="flex items-center gap-3 rounded-md border bg-muted/40 p-3 text-sm">
          {thumb ? (
            <img
              src={thumb}
              alt="video preview"
              className="h-16 w-28 rounded-sm object-cover"
            />
          ) : (
            <div className="flex h-16 w-28 items-center justify-center rounded-sm bg-muted text-muted-foreground">
              <Film className="h-6 w-6" />
            </div>
          )}
          <div>
            <p className="font-medium capitalize">{parsed.provider}</p>
            <p className="text-xs text-muted-foreground">id: {parsed.videoId}</p>
            <p className="truncate font-mono text-xs text-muted-foreground">
              {parsed.canonicalURL}
            </p>
          </div>
        </div>
      )}
      {raw.trim() !== '' && !parsed && (
        <p className="text-xs text-destructive">
          URL not recognized. YouTube (watch / youtu.be / embed) and Vimeo URLs are supported.
          Playlists are rejected.
        </p>
      )}
    </div>
  );
}
